/**
 * @file InboxTopbarButton.tsx
 * @brief Topbar Inbox icon: unread count badge, a dot when something starts soon, opens the Inbox page.
 *
 * @details
 * The phone header's Inbox button (DES 12-10-2026) on web and desktop. Polls
 * the unread count every minute and on window focus; the dot appears when
 * nothing is unread but a meeting or session starts within 24 h.
 * Not shown to a superadmin (no meetings or notifications of their own here).
 *
 * @author DES
 * @date 12-10-2026
 */

import { NavLink } from "react-router-dom";
import { Inbox } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { startsSoon, useInboxBase, useInboxUnread, useInboxUpcoming } from "./api";

export function InboxTopbarButton({ className }: { className?: string }) {
  const { base, enabled } = useInboxBase();
  const unread = useInboxUnread().data ?? 0;
  const upcoming = useInboxUpcoming().data?.items;
  if (!enabled) return null;

  const soon = unread === 0 && startsSoon(upcoming);
  const label = unread > 0 ? `Inbox, ${unread} unread` : soon ? "Inbox, something starts soon" : "Inbox";

  return (
    <NavLink
      to={`${base}/inbox`}
      aria-label={label}
      title="Inbox"
      className={({ isActive }) =>
        cn(
          className,
          "relative",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground",
        )
      }
    >
      <Inbox className="h-5 w-5" />
      {unread > 0 ? (
        <span className="absolute right-1.5 top-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-bold leading-none text-white">
          {unread > 99 ? "99+" : unread}
        </span>
      ) : soon ? (
        <span className="absolute right-2.5 top-3.5 h-2 w-2 rounded-full bg-[#EF4444]" aria-hidden />
      ) : null}
    </NavLink>
  );
}
